import { useLingui } from "@lingui/react";
import {
  ApiDatafanAccountCredential,
  EApiDatafanPipelineType,
  FailureApi,
} from "@smp/api";
import { Await } from "@smp/utils";
import { useMemo } from "react";
import { useQuery, UseQueryOptions } from "react-query";

import { useQueryDatafanPipelineTypes } from "./use_query_datafan_pipeline_type";
import { useQueryDatafanProject } from "./use_query_datafan_project";

const KEY = "datafan.credentials";

type Response = Await<
  ReturnType<typeof ApiDatafanAccountCredential.list>
>["results"];

export const useQueryDatafanCredentials = (
  params?: {
    streams?: EApiDatafanPipelineType[];
    id__in?: number[];
  },
  options?: Omit<UseQueryOptions<Response, FailureApi>, "queryKey" | "queryFn">,
) => {
  const { i18n } = useLingui();
  const lang = i18n.locale === "ru" ? "ru" : "en";
  const {
    data: project,
    error: projectError,
    isLoading: projectIsLoading,
    isFetching: projectIsFetching,
  } = useQueryDatafanProject({
    enabled: options?.enabled,
  });

  const {
    data: pipelineTypes,
    error: pipelineTypesError,
    isLoading: pipelineTypesIsLoading,
    isFetching: pipelineTypesIsFetching,
  } = useQueryDatafanPipelineTypes({
    enabled: options?.enabled,
  });

  const query = useMemo(() => {
    if (project && params?.id__in) {
      return {
        filtered: JSON.stringify({
          id__in: params.id__in,
          projects: [project.id],
        }),
        lang,
      } as const;
    }

    return project && pipelineTypes
      ? ({
          filtered: JSON.stringify({
            projects: [project.id],
            stream__in:
              params?.streams ||
              (pipelineTypes
                ? (
                    Object.keys(pipelineTypes) as unknown as Array<
                      keyof typeof pipelineTypes
                    >
                  ).filter((typeId) => !pipelineTypes?.[typeId]?.is_depreciated)
                : []),
          }),
          lang,
        } as const)
      : undefined;
  }, [lang, params?.id__in, params?.streams, pipelineTypes, project]);

  const { data, error, isLoading, isFetching, ...spread } = useQuery<
    Response,
    FailureApi
  >(
    [KEY, query],
    () =>
      ApiDatafanAccountCredential.list(query).then(({ results }) => results),
    {
      ...options,
      enabled: !!query && options?.enabled,
    },
  );

  return {
    ...spread,
    data: data,
    error: error || projectError || pipelineTypesError,
    isFetching: isFetching || projectIsFetching || pipelineTypesIsFetching,
    isLoading: isLoading || projectIsLoading || pipelineTypesIsLoading,
  };
};

useQueryDatafanCredentials.KEY = KEY;
